import { useRef, useState } from "react";
import { API_DOMAIN } from "./constants";

const CONTEXT_PAGE_SIZE = 10;

export default function useMessageSearch(apiToken) {
    const [content, setContent] = useState("");
    const [userId, setUserId] = useState("");
    const [specifyDay, setSpecifyDay] = useState(false);
    const [date, setDate] = useState(new Date());
    const [searchByUsername, setSearchByUsername] = useState(false);

    const [messages, setMessages] = useState([]);
    const cursorRef = useRef(null);
    const loadingRef = useRef(false);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [contextMessages, setContextMessages] = useState([]);
    const [contextAnchorMessageId, setContextAnchorMessageId] = useState(null);
    const [hasOlderContext, setHasOlderContext] = useState(true);
    const [hasNewerContext, setHasNewerContext] = useState(true);
    const [loadingContext, setLoadingContext] = useState(false);
    const [loadingOlderContext, setLoadingOlderContext] = useState(false);
    const [loadingNewerContext, setLoadingNewerContext] = useState(false);
    const loadingContextRef = useRef(false);

    function resetMessages() {
        setMessages([]);
        cursorRef.current = null;
        setHasMore(true);
    }

    async function fetchMessages() {
        if (loadingRef.current) return;

        loadingRef.current = true;
        setLoading(true);

        try {
            let startTime = null;
            let endTime = null;
            if (specifyDay) {
                startTime = Math.floor(new Date(date).setHours(0, 0, 0, 0) / 1000);
                endTime = Math.floor(new Date(date).setHours(23, 59, 59, 999) / 1000);
            }

            const query = `
        query GetMessages(
          $content: String,
          $author: String,
          $authorUsername: String,
          $startTime: Float,
          $endTime: Float,
          $cursor: String
        ) {
          messages(
            content: $content,
            author: $author,
            authorUsername: $authorUsername,
            startTime: $startTime,
            endTime: $endTime,
            cursor: $cursor
          ) {
            messages {
              id
              messageId
              content
              createdAt
              author {
                userId
                username
                avatar
              }
              channel {
                name
              }
              reference {
                messageId
                content
                authorId
                author {
                  username
                  avatar
                }
              }
              editHistory {
                timestamp
                content
                mentions
              }
              mentions {
                user {
                  userId
                  username
                  avatar
                }
              }
              attachments {
                url
                filename
              }
            }
            cursor
          }
        }
      `;

            const variables = {
                content: content || null,
                author: searchByUsername ? null : (userId || null),
                authorUsername: searchByUsername ? (userId || null) : null,
                startTime,
                endTime,
                cursor: cursorRef.current,
            };

            const headers = {
                "Content-Type": "application/json",
            };
            if (apiToken) {
                headers.Authorization = `Bearer ${apiToken}`;
            }

            const response = await fetch(`${API_DOMAIN}/api/search/graphql`, {
                method: "POST",
                headers,
                body: JSON.stringify({ query, variables }),
            });

            if (!response.ok) {
                throw new Error("Failed to fetch messages");
            }

            const result = await response.json();
            const fetchedMessages = result?.data?.messages[0]?.messages || [];
            const newCursor = result?.data?.messages[0]?.cursor;

            if (fetchedMessages.length > 0) {
                setMessages((prev) => [...prev, ...fetchedMessages]);
                cursorRef.current = newCursor;
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error fetching messages:", error);
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    }

    function mergeContextMessages(currentMessages, incomingMessages, direction, pivotMessageId) {
        const existingIds = new Set(currentMessages.map((message) => message.messageId));
        const newMessages = incomingMessages.filter((message) => {
            return message.messageId !== pivotMessageId && !existingIds.has(message.messageId);
        });

        if (direction === "older") {
            return [...newMessages, ...currentMessages];
        }

        if (direction === "newer") {
            return [...currentMessages, ...newMessages];
        }

        return incomingMessages;
    }

    async function requestMessageContext(messageId, before, after) {
        const query = `
                query ContextMessages($messageId: String!, $before: Int!, $after: Int!) {
                    contextMessages(messageId: $messageId, before: $before, after: $after) {
                        messageId
                        channelId
                        authorId
                        content
                        createdAt
                        editedAt
                        guildId
                        id
                        mentions {
                            userId
                            user {
                                userId
                                username
                                avatar
                            }
                        }
                        editHistory {
                            timestamp
                            content
                            mentions
                        }
                        reference {
                            messageId
                            channelId
                            authorId
                            content
                            author {
                                userId
                                username
                                avatar
                            }
                        }
                        author {
                            userId
                            username
                            avatar
                        }
                        channel {
                            channelId
                            name
                            guildId
                        }
                        attachments {
                            url
                            filename
                        }
                    }
                }
            `;

        const variables = { messageId, before, after };

        const headers = {
            "Content-Type": "application/json",
        };
        if (apiToken) {
            headers.Authorization = `Bearer ${apiToken}`;
        }

        const response = await fetch(`${API_DOMAIN}/api/search/graphql`, {
            method: "POST",
            headers,
            body: JSON.stringify({ query, variables }),
        });

        if (!response.ok) {
            throw new Error("Failed to fetch message context");
        }

        const result = await response.json();
        return result?.data?.contextMessages || [];
    }

    async function fetchMessageContext(messageId) {
        if (loadingContextRef.current) return;

        loadingContextRef.current = true;
        setLoadingContext(true);
        setContextAnchorMessageId(messageId);
        setContextMessages([]);
        setHasOlderContext(true);
        setHasNewerContext(true);

        try {
            const fetchedMessages = await requestMessageContext(messageId, CONTEXT_PAGE_SIZE, CONTEXT_PAGE_SIZE);
            const anchorIndex = fetchedMessages.findIndex((message) => message.messageId === messageId);

            setContextMessages(fetchedMessages);
            setHasOlderContext(anchorIndex >= CONTEXT_PAGE_SIZE);
            setHasNewerContext(anchorIndex !== -1 && fetchedMessages.length - anchorIndex - 1 >= CONTEXT_PAGE_SIZE);
        } catch (error) {
            console.error("Error fetching message context:", error);
        } finally {
            loadingContextRef.current = false;
            setLoadingContext(false);
        }
    }

    async function fetchOlderContext() {
        if (loadingContextRef.current || loadingOlderContext || !hasOlderContext || contextMessages.length === 0) return;

        const pivotMessageId = contextMessages[0].messageId;
        setLoadingOlderContext(true);

        try {
            const fetchedMessages = await requestMessageContext(pivotMessageId, CONTEXT_PAGE_SIZE, 0);
            const existingIds = new Set(contextMessages.map((message) => message.messageId));
            const newMessages = fetchedMessages.filter((message) => {
                return message.messageId !== pivotMessageId && !existingIds.has(message.messageId);
            });

            setContextMessages((currentMessages) => mergeContextMessages(currentMessages, fetchedMessages, "older", pivotMessageId));
            setHasOlderContext(newMessages.length >= CONTEXT_PAGE_SIZE);
        } catch (error) {
            console.error("Error fetching older context messages:", error);
        } finally {
            setLoadingOlderContext(false);
        }
    }

    async function fetchNewerContext() {
        if (loadingContextRef.current || loadingNewerContext || !hasNewerContext || contextMessages.length === 0) return;

        const pivotMessageId = contextMessages[contextMessages.length - 1].messageId;
        setLoadingNewerContext(true);

        try {
            const fetchedMessages = await requestMessageContext(pivotMessageId, 0, CONTEXT_PAGE_SIZE);
            const existingIds = new Set(contextMessages.map((message) => message.messageId));
            const newMessages = fetchedMessages.filter((message) => {
                return message.messageId !== pivotMessageId && !existingIds.has(message.messageId);
            });

            setContextMessages((currentMessages) => mergeContextMessages(currentMessages, fetchedMessages, "newer", pivotMessageId));
            setHasNewerContext(newMessages.length >= CONTEXT_PAGE_SIZE);
        } catch (error) {
            console.error("Error fetching newer context messages:", error);
        } finally {
            setLoadingNewerContext(false);
        }
    }

    return {
        content,
        userId,
        specifyDay,
        date,
        messages,
        hasMore,
        loading,
        searchByUsername,
        contextMessages,
        contextAnchorMessageId,
        hasOlderContext,
        hasNewerContext,
        loadingContext,
        loadingOlderContext,
        loadingNewerContext,
        setContent,
        setUserId,
        setSpecifyDay,
        setDate,
        setSearchByUsername,
        resetMessages,
        fetchMessages,
        fetchMessageContext,
        fetchOlderContext,
        fetchNewerContext,
        setContextMessages,
    };
}
